import React from 'react';
import { Facebook, Instagram, Linkedin, Twitter, Mail, Phone, MapPin, Download } from 'lucide-react';
import { companyInfo, socialLinks } from '../mockData';

const iconMap = {
  Facebook,
  Instagram,
  Linkedin,
  Twitter
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black text-white">
      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-bold text-gold mb-4">
              {companyInfo.name}
            </h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Licensed electrical contracting services serving residential, commercial, and industrial clients throughout the Kootenays. Quality workmanship, safety first, and customer satisfaction guaranteed.
            </p>
            <p className="text-gold font-semibold text-lg">
              {companyInfo.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-gold transition-colors"
                >
                  Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('renewable')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-gold transition-colors"
                >
                  Renewable Energy
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('certifications')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-gold transition-colors"
                >
                  Certifications
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-gold transition-colors"
                >
                  Contact
                </button>
              </li>
              <li>
                <a 
                  href="https://g.page/r/CQGFqDvNi08SEBM/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="leave-review-link"
                  className="text-gold hover:text-white font-semibold transition-colors"
                >
                  Leave a Review
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <a 
                  href={`tel:${companyInfo.phone}`}
                  className="text-gray-400 hover:text-gold transition-colors"
                >
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="text-gray-400 hover:text-gold transition-colors break-all"
                >
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gold mt-1 flex-shrink-0" />
                <span className="text-gray-400">
                  {companyInfo.location}<br />
                  {companyInfo.postalCode}
                </span>
              </li>
            </ul>
          </div>

          {/* Marketing Materials */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Marketing Materials</h4>
            <ul className="space-y-3">
              {[
                { label: 'Brochure', html: '/marketing/brochure.html', pdf: '/marketing/pdf/brochure.pdf' },
                { label: 'Flyer', html: '/marketing/flyer.html', pdf: '/marketing/pdf/flyer.pdf' },
                { label: 'Poster', html: '/marketing/poster.html', pdf: '/marketing/pdf/poster.pdf' }
              ].map((item) => (
                <li key={item.label} className="flex items-center justify-between">
                  <a
                    href={item.html}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`marketing-${item.label.toLowerCase()}-link`}
                    className="text-gray-400 hover:text-gold transition-colors"
                  >
                    {item.label}
                  </a>
                  <a
                    href={item.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    data-testid={`marketing-${item.label.toLowerCase()}-pdf`}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-gold/70 hover:text-gold border border-gold/30 hover:border-gold rounded px-1.5 py-0.5 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    PDF
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Links & Copyright */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} {companyInfo.name}. All rights reserved.
            </p>
            
            {/* Social Icons */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => {
                const IconComponent = iconMap[social.icon];
                return (
                  <a
                    key={social.platform}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/5 border border-gold/30 flex items-center justify-center hover:bg-gold hover:border-gold transition-all duration-300 group"
                  >
                    <IconComponent className="w-5 h-5 text-gray-400 group-hover:text-black transition-colors" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
